import { useState } from "react";
import {
  getSalaryAdjustmentInputValue,
  getSalaryAdjustmentMutations,
  getSignedSalaryAdjustmentValue,
  isValidSalaryAdjustmentInput,
  isSalaryAdjustmentKey,
  removeSalaryAdjustment,
  SALARY_ADJUSTMENT_DATABASE_ROW_KEYS,
  type SalaryAdjustment,
  SalaryAdjustmentDatabaseRowKey,
  upsertSalaryAdjustment,
} from "./adjustSalary.logic";
import { getFirstAvailableSalaryAdjustmentKey } from "./adjustSalaryModal.logic";

interface UseAdjustSalaryModalDraftParams {
  readonly initialAdjustments: readonly SalaryAdjustment[];
  readonly isSubmitting: boolean;
}

export type SalaryAdjustmentEditState =
  | { readonly type: "idle" }
  | {
      readonly type: "editing";
      readonly key: SalaryAdjustmentDatabaseRowKey;
      readonly value: string;
    };

export type SalaryAdjustmentAddFormState =
  | { readonly type: "unavailable" }
  | {
      readonly type: "available";
      readonly key: SalaryAdjustmentDatabaseRowKey;
      readonly amount: string;
    };

interface UseAdjustSalaryModalDraftReturn {
  readonly addFormState: SalaryAdjustmentAddFormState;
  readonly adjustments: readonly SalaryAdjustment[];
  readonly availableKeys: readonly SalaryAdjustmentDatabaseRowKey[];
  readonly editState: SalaryAdjustmentEditState;
  readonly hasChanges: boolean;
  readonly isAmountValid: boolean;
  readonly isEditingAmountValid: boolean;
  readonly handleAdd: () => void;
  readonly handleAmountChange: (value: string) => void;
  readonly handleCancelEdit: () => void;
  readonly handleConfirmEdit: () => void;
  readonly handleEdit: (adjustment: SalaryAdjustment) => void;
  readonly handleEditingAmountChange: (value: string) => void;
  readonly handleRemove: (key: SalaryAdjustmentDatabaseRowKey) => void;
  readonly handleSelectedTypeChange: (key: string) => void;
}

export const useAdjustSalaryModalDraft = ({
  initialAdjustments,
  isSubmitting,
}: UseAdjustSalaryModalDraftParams): UseAdjustSalaryModalDraftReturn => {
  const [adjustments, setAdjustments] = useState(initialAdjustments);
  const [editState, setEditState] = useState<SalaryAdjustmentEditState>({
    type: "idle",
  });
  const [addFormState, setAddFormState] =
    useState<SalaryAdjustmentAddFormState>(() => {
      const key = getFirstAvailableSalaryAdjustmentKey(initialAdjustments);
      return key === undefined
        ? { type: "unavailable" }
        : { type: "available", key, amount: "" };
    });

  const numericAmount =
    addFormState.type === "available"
      ? getSignedSalaryAdjustmentValue({
          key: addFormState.key,
          value: addFormState.amount,
        })
      : undefined;
  const numericEditingAmount =
    editState.type === "editing"
      ? getSignedSalaryAdjustmentValue({
          key: editState.key,
          value: editState.value,
        })
      : undefined;
  const isAmountValid = numericAmount !== undefined;
  const isEditingAmountValid = numericEditingAmount !== undefined;
  const availableKeys = SALARY_ADJUSTMENT_DATABASE_ROW_KEYS.filter(
    (key) => !adjustments.some((adjustment) => adjustment.key === key),
  );
  const hasChanges =
    getSalaryAdjustmentMutations({ adjustments, initialAdjustments }).length >
    0;

  const resetForm = (nextAdjustments: readonly SalaryAdjustment[]): void => {
    const key = getFirstAvailableSalaryAdjustmentKey(nextAdjustments);
    setAddFormState(
      key === undefined
        ? { type: "unavailable" }
        : { type: "available", key, amount: "" },
    );
  };

  const handleAdd = (): void => {
    if (
      addFormState.type !== "available" ||
      numericAmount === undefined ||
      isSubmitting
    ) {
      return;
    }
    const nextAdjustments = upsertSalaryAdjustment({
      adjustment: { key: addFormState.key, value: numericAmount },
      adjustments,
    });
    setAdjustments(nextAdjustments);
    resetForm(nextAdjustments);
  };

  const handleAmountChange = (value: string): void => {
    if (
      addFormState.type === "available" &&
      isValidSalaryAdjustmentInput({ key: addFormState.key, value })
    ) {
      setAddFormState({ ...addFormState, amount: value });
    }
  };

  const handleCancelEdit = (): void => {
    setEditState({ type: "idle" });
  };

  const handleConfirmEdit = (): void => {
    if (
      editState.type !== "editing" ||
      numericEditingAmount === undefined ||
      isSubmitting
    ) {
      return;
    }
    setAdjustments(
      upsertSalaryAdjustment({
        adjustment: { key: editState.key, value: numericEditingAmount },
        adjustments,
      }),
    );
    handleCancelEdit();
  };

  const handleEdit = (adjustment: SalaryAdjustment): void => {
    setEditState({
      type: "editing",
      key: adjustment.key,
      value: getSalaryAdjustmentInputValue(adjustment),
    });
  };

  const handleEditingAmountChange = (value: string): void => {
    if (
      editState.type === "editing" &&
      isValidSalaryAdjustmentInput({ key: editState.key, value })
    ) {
      setEditState({ ...editState, value });
    }
  };

  const handleRemove = (key: SalaryAdjustmentDatabaseRowKey): void => {
    const nextAdjustments = removeSalaryAdjustment({ adjustments, key });
    setAdjustments(nextAdjustments);
    if (editState.type === "editing" && editState.key === key) {
      handleCancelEdit();
    }
    if (addFormState.type === "unavailable") {
      setAddFormState({ type: "available", key, amount: "" });
    }
  };

  const handleSelectedTypeChange = (key: string): void => {
    const numericKey = Number(key);
    if (
      isSalaryAdjustmentKey(numericKey) &&
      availableKeys.includes(numericKey)
    ) {
      setAddFormState({ type: "available", key: numericKey, amount: "" });
    }
  };

  return {
    addFormState,
    adjustments,
    availableKeys,
    editState,
    handleAdd,
    handleAmountChange,
    handleCancelEdit,
    handleConfirmEdit,
    handleEdit,
    handleEditingAmountChange,
    handleRemove,
    handleSelectedTypeChange,
    hasChanges,
    isAmountValid,
    isEditingAmountValid,
  };
};
